import { User } from '../user';
import {
    processTemplates,
    tanderProcessTemplates,
    dixyAcceptedProcessTemplates,
    dixyApprovedProcessTemplates,
} from '../processTemplates';
import { Registers } from './types';
import { registersAvailableStatuses } from './utils';


export const dixyAcceptedRegisterParameters = {
    clientUnprocessed: {
        filters: {
            type: [Registers.Templates.accepted],
            stage: ['Ack______'],
            status: registersAvailableStatuses,
            processTemplate: dixyAcceptedProcessTemplates,
        },
    },
    notVerified: {
        filters: {
            type: [
                Registers.Templates.accepted,
                Registers.Templates.confirmed,
                Registers.Templates.rejected,
            ],
            stage: ['Ack______'],
            status: registersAvailableStatuses,
            processTemplate: dixyAcceptedProcessTemplates,
        },
    },
    all: {
        filters: {
            type: [
                Registers.Templates.accepted,
                Registers.Templates.confirmed,
                Registers.Templates.rejected,
            ],
            status: registersAvailableStatuses,
            processTemplate: dixyAcceptedProcessTemplates,
        },
    },
    accepted: {
        filters: {
            type: [Registers.Templates.accepted],
            status: registersAvailableStatuses,
            processTemplate: dixyAcceptedProcessTemplates,
        },
    },
    confirmed: {
        filters: {
            type: [Registers.Templates.confirmed],
            status: registersAvailableStatuses,
            processTemplate: dixyAcceptedProcessTemplates,
        },
    },
    rejected: {
        filters: {
            type: [Registers.Templates.rejected],
            status: registersAvailableStatuses,
            processTemplate: dixyAcceptedProcessTemplates,
        },
    },
};

export const dixyApprovedRegisterParameters = {
    all: {
        filters: {
            type: [Registers.Templates.approved],
            status: registersAvailableStatuses,
            processTemplate: dixyApprovedProcessTemplates,
        },
    },
    unprocessed: {
        filters: {
            type: [Registers.Templates.approved],
            status: [Registers.Statuses.onSourceConfirmation],
            processTemplate: dixyApprovedProcessTemplates,
        },
    },
    unsignedFactor: {
        filters: {
            type: [Registers.Templates.approved],
            status: [
                Registers.Statuses.onSourceConfirmation,
                Registers.Statuses.onFactorSigning,
            ],
            processTemplate: dixyApprovedProcessTemplates,
        },
    },
};

export const victoriaRegisterParameters = {
    all: {
        filters: {
            type: [Registers.Templates.approved],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.victoriaProcessCeded],
        },
    },
    unprocessed: {
        filters: {
            type: [Registers.Templates.approved],
            status: [
                Registers.Statuses.onSourceConfirmation,
                Registers.Statuses.onDebtorSigning,
            ],
            processTemplate: [processTemplates.victoriaProcessCeded],
        },
    },
    unsignedFactor: {
        filters: {
            type: [Registers.Templates.approved],
            status: [
                Registers.Statuses.onSourceConfirmation,
                Registers.Statuses.onFactorSigning,
            ],
            processTemplate: [processTemplates.victoriaProcessCeded],
        },
    },
};

export const tanderApprovedRegisterParameters = {
    all: {
        filters: {
            type: [
                Registers.Templates.approved,
                Registers.Templates.transport,
            ],
            status: registersAvailableStatuses,
            processTemplate: tanderProcessTemplates,
        },
    },
    unprocessed: {
        filters: {
            type: [
                Registers.Templates.approved,
                Registers.Templates.transport,
            ],
            status: [
                Registers.Statuses.onDebtorSigning,
                Registers.Statuses.onSourceConfirmation,
            ],
            processTemplate: tanderProcessTemplates,
        },
    },
    approved: {
        filters: {
            type: [Registers.Templates.approved],
            status: registersAvailableStatuses,
            processTemplate: tanderProcessTemplates,
        },
    },
    transport: {
        filters: {
            type: [Registers.Templates.transport],
            status: registersAvailableStatuses,
            processTemplate: tanderProcessTemplates,
        },
    },
    unsignedFactor: {
        filters: {
            type: [Registers.Templates.approved],
            status: [
                Registers.Statuses.onSourceConfirmation,
                Registers.Statuses.onFactorSigning,
            ],
            processTemplate: tanderProcessTemplates,
        },
    },
};

const signingTemplateCodes: { [KRole in User.FactoringRole]: string[] } = {
    Admin: [
        Registers.Templates.approved,
        Registers.Templates.ceded,
    ],
    Factor: [
        Registers.Templates.approved,
        Registers.Templates.ceded,
        Registers.Templates.transport,
    ],
    Client: [Registers.Templates.ceded],
    Debtor: [],
    Platform: [],
};

const unprocessedStatuses: { [KRole in User.FactoringRole]: string[] } = {
    Admin: [
        Registers.Statuses.waitFactorProcessing,
        Registers.Statuses.onFactorSigning,
        Registers.Statuses.onClientSigning,
    ],
    Factor: [
        Registers.Statuses.waitFactorProcessing,
        Registers.Statuses.onFactorSigning,
    ],
    Client: [Registers.Statuses.onClientSigning],
    Debtor: [],
    Platform: [],
};

export const factorApprovedRegisterParameters = (
    role: User.FactoringRole,
    processTemplate: string[],
) => ({
    unprocessed: {
        filters: {
            type: signingTemplateCodes[role],
            status: unprocessedStatuses[role],
            processTemplate,
        },
    },
    transport: {
        filters: {
            type: [Registers.Templates.transport],
            status: registersAvailableStatuses,
            processTemplate,
        },
    },
    all: {
        filters: {
            type: signingTemplateCodes[role],
            status: registersAvailableStatuses,
            processTemplate,
        },
    },
});

export const getDiscountRegisterParametersForProcess = (processTemplate: string[]) => ({
    all: {
        filters: {
            type: [
                Registers.Templates.discount,
                Registers.Templates.discountConf,
                Registers.Templates.discountRej,
            ],
            status: registersAvailableStatuses,
            processTemplate,
        },
    },
    debtorUnprocessed: {
        filters: {
            type: [Registers.Templates.discountConf],
            status: registersAvailableStatuses,
            signAllowed: true,
            processTemplate,
        },
    },
    clientUnprocessed: {
        filters: {
            type: [Registers.Templates.discount],
            status: [Registers.Statuses.onClientConfirmation],
            processTemplate,
        },
    },
    discount: {
        filters: {
            type: [Registers.Templates.discount],
            status: registersAvailableStatuses,
            processTemplate,
        },
    },
    discountConf: {
        filters: {
            type: [Registers.Templates.discountConf],
            status: registersAvailableStatuses,
            processTemplate,
        },
    },
    discountRejec: {
        filters: {
            type: [Registers.Templates.discountRej],
            status: registersAvailableStatuses,
            processTemplate,
        },
    },
});

export const cedeRegisterParameters = {
    verified: {
        filters: {
            type: [Registers.Templates.verified],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.verifiedProcess],
        },
    },
    ceeded: {
        filters: {
            type: [
                Registers.Templates.ceded,
                Registers.Templates.financed,
                Registers.Templates.nonFinanced,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.verifiedProcess],
        },
    },
    all: {
        filters: {
            type: [
                Registers.Templates.verified,
                Registers.Templates.ceded,
                Registers.Templates.financed,
                Registers.Templates.nonFinanced,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.verifiedProcess],
        },
    },
};

export const factorConveyorParameters = (processTemplate: string) => ({
    unprocessed: {
        filters: {
            type: [
                Registers.Templates.ceded,
                Registers.Templates.financed,
                Registers.Templates.nonFinanced,
            ],
            status: registersAvailableStatuses,
            processing: true,
            processTemplate: [processTemplate],
        },
    },
    all: {
        filters: {
            type: [
                Registers.Templates.ceded,
                Registers.Templates.financed,
                Registers.Templates.nonFinanced,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplate],
        },
    },
    unsigned: {
        filters: {
            type: [Registers.Templates.ceded],
            status: [Registers.Statuses.onFactorSigning],
            stage: ['Ack______'],
            processTemplate: [processTemplate],
        },
    },
});

export const debtorConveyorParameters = {
    unprocessed: {
        filters: {
            type: [Registers.Templates.completed],
            status: [Registers.Statuses.onDebtorConfirmation],
            stage: ['Ack______'],
            processTemplate: [processTemplates.conveyorProcess],
        },
    },
    all: {
        filters: {
            type: [
                Registers.Templates.completed,
                Registers.Templates.ceded,
                Registers.Templates.completedRej,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.conveyorProcess],
        },
    },
    unsigned: {
        filters: {
            type: [Registers.Templates.ceded],
            status: [Registers.Statuses.onDebtorSigning],
            stage: ['New______'],
            processTemplate: [processTemplates.conveyorProcess],
        },
    },
};

export const clientConveyorParameters = {
    unprocessed: {
        filters: {
            type: [Registers.Templates.ceded],
            status: [Registers.Statuses.waitClientProcessing],
            stage: ['Formed___'],
        },
    },
    rvp: {
        filters: {
            type: [
                Registers.Templates.completed,
                Registers.Templates.ceded,
                Registers.Templates.completedRej,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.conveyorProcess],
        },
    },
    rnu: {
        filters: {
            type: [
                Registers.Templates.ceded,
                Registers.Templates.financed,
                Registers.Templates.nonFinanced,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.conveyorProcess],
        },
    },
    all: {
        filters: {
            type: [
                Registers.Templates.completed,
                Registers.Templates.ceded,
                Registers.Templates.completedRej,
                Registers.Templates.financed,
                Registers.Templates.nonFinanced,
            ],
            status: registersAvailableStatuses,
            processTemplate: [processTemplates.conveyorProcess],
        },
    },
};

export const getFinanceParameters = (processTemplate: string) => ({
    factorUnprocessed: {
        filters: {
            type: [Registers.Templates.fundedSupply],
            status: [Registers.Statuses.onFactorSigning],
            stage: ['Formed___'],
            processTemplate: [processTemplate],
        },
    },
    clientUnprocessed: {
        filters: {
            type: [Registers.Templates.fundedSupply],
            status: [Registers.Statuses.onClientSigning],
            stage: ['Ack______'],
            processTemplate: [processTemplate],
        },
    },
    all: {
        filters: {
            processTemplate: [processTemplate],
        },
    },
});

export * from './mtsSperParameters';
export * from './rtkEpParameters';
